import { AdbDaemonWebUsbDevice, AdbDaemonWebUsbDeviceManager } from '@yume-chan/adb-daemon-webusb';
import { Adb as AdbDaemon, AdbDaemonTransport } from '@yume-chan/adb';
import AdbWebCredentialStore from '@yume-chan/adb-credential-web';
import { logger } from '@/api/logger';

const credential: AdbWebCredentialStore = new AdbWebCredentialStore('Butterfly');

/**
 * Une seule connexion ADB par casque, partagee par tous les appels.
 *
 * `navigator.usb` ne donne qu'un seul handle par peripherique : deux `Adb`
 * ouvertes en parallele sur le meme serial partagent le meme `USBDevice`, donc
 * le `close()` (releaseInterface) de la premiere tue les transferts en cours de
 * la seconde. C'est ce qui se passait entre `AutoConfig.loadDeviceInfo()` et le
 * composant enfant (PicoG2/G3/Phone), en particulier quand un evenement USB
 * relancait `selectDevice()` pendant une installation : le push d'APK mourait
 * avec `ExactReadable ended` puis `transferOut: A transfer error has occurred`.
 *
 * La session est comptee par reference : elle n'est fermee que lorsque le
 * dernier porteur l'a relachee. Une erreur USB la marque cassee, pour que le
 * prochain `acquire()` reparte sur une connexion neuve plutot que de reutiliser
 * un transport mort.
 */
export class AdbSession {
    private static readonly sessions = new Map<string, AdbSession>();

    /** Connexion partagee pour ce serial, ouverte si besoin. */
    public static async acquire(serial: string): Promise<AdbSession> {
        let session = AdbSession.sessions.get(serial);
        if (!session || session.broken) {
            session = new AdbSession(serial);
            AdbSession.sessions.set(serial, session);
        }
        return session.open();
    }

    public readonly serial: string;
    declare public device: AdbDaemonWebUsbDevice;
    declare public daemon: AdbDaemon;
    private refs = 0;
    private broken = false;
    private opening?: Promise<void>;

    private constructor(serial: string) {
        this.serial = serial;
    }

    /** Nombre de porteurs actuels, journalise pour tracer le partage. */
    public get holders(): number {
        return this.refs;
    }

    /**
     * Marque la connexion inutilisable : le transport ADB ne survit pas a une
     * erreur de transfert USB, tout reutilisateur heriterait d'un tuyau mort.
     */
    public fail(reason: unknown): void {
        if (this.broken) return;
        this.broken = true;
        AdbSession.sessions.delete(this.serial);
        logger.debug({
            action: 'session broken',
            serial: this.serial,
            holders: this.refs,
            error: `${reason}`
        });
    }

    /** Relache une reference ; ferme reellement la connexion a la derniere. */
    public async release(): Promise<void> {
        if (this.refs > 0) this.refs--;
        if (this.refs > 0) {
            logger.debug({ action: 'release', serial: this.serial, holders: this.refs });
            return;
        }
        AdbSession.sessions.delete(this.serial);
        const daemon = this.daemon;
        if (!daemon) return;
        try {
            logger.debug({ action: 'close', serial: this.serial });
            await daemon.close();
        } catch (e) {
            logger.debug({ action: 'close', serial: this.serial, error: `${e}` });
        }
    }

    private async open(): Promise<AdbSession> {
        this.refs++;
        try {
            if (!this.daemon) {
                if (!this.opening) this.opening = this.connect();
                await this.opening;
            } else {
                logger.debug({ action: 'reuse', serial: this.serial, holders: this.refs });
            }
            return this;
        } catch (e) {
            this.refs--;
            this.fail(e);
            throw e;
        }
    }

    private async connect(): Promise<void> {
        try {
            const device = await this.findDevice();
            const conn = await device.connect();
            const transport = await AdbDaemonTransport.authenticate({
                serial: conn.device.serial,
                connection: conn,
                credentialStore: credential
            });
            this.device = device;
            this.daemon = new AdbDaemon(transport);
            logger.debug({ action: 'connected', serial: this.serial, holders: this.refs });
        } catch (e) {
            logger.error({ action: 'connect', serial: this.serial, error: `${e}` });
            throw e;
        } finally {
            this.opening = undefined;
        }
    }

    private async findDevice(): Promise<AdbDaemonWebUsbDevice> {
        const manager = AdbDaemonWebUsbDeviceManager.BROWSER;
        if (!manager) throw 'No ADB over Web USB';
        const device = (await manager.getDevices()).find((d) => d.serial == this.serial);
        if (device == undefined) {
            logger.error({ action: 'getDevice', serial: this.serial, error: 'Device not found' });
            throw 'Phone is undefined';
        }
        return device;
    }
}
